import { useState, useContext } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../api_config";
import UserContext from "../../Authcontext";
import "./payment.css";

function PhonePePayment({ userId, onPaymentSuccess, activePlanName }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { token, isAuthenticated, userId: contextUserId } = useContext(UserContext);

  // Use userId from props, or fallback to context, or extract from token
  const getEffectiveUserId = () => {
    if (userId) return userId;
    if (contextUserId) return contextUserId;

    // Try to extract userId from token
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.userId || payload.id || payload.sub;
      } catch (e) {
        console.warn('Could not extract userId from token:', e);
      }
    }

    return null;
  };
  const handlePhonePePayment = async (planName, amount) => {
    try {
      setLoading(true);
      setError(null);

      // Check authentication using UserContext
      console.log("Authentication check:", {
        isAuthenticated,
        tokenExists: !!token,
        tokenLength: token?.length,
        tokenPreview: token ? token.substring(0, 20) + "..." : "No token found"
      });

      if (!isAuthenticated || !token) {
        throw new Error("You are not logged in. Please login first to make a payment.");
      }

      // Get effective userId
      const effectiveUserId = getEffectiveUserId();

      // Validate required parameters
      console.log("Payment parameters:", {
        planName,
        amount,
        propUserId: userId,
        contextUserId: contextUserId,
        effectiveUserId: effectiveUserId,
        userIdType: typeof effectiveUserId
      });

      if (!amount || amount <= 0) {
        throw new Error("Invalid amount. Please select a valid plan.");
      }

      if (!effectiveUserId) {
        throw new Error("User ID is required. Please login again.");
      }

      console.log("Initiating PhonePe payment for:", { planName, amount, userId: effectiveUserId });

      const response = await axios.post(
        `${API_BASE_URL}/api/web/phonepe/initialize-payment`,
        {
          amount: amount,
          userId: effectiveUserId,
          planName: planName,
          userEmail: null // Can be added if available
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );

      console.log("PhonePe response:", response.data);

      if (response.data.success && response.data.redirectUrl) {
        // Redirect to PhonePe payment page
        console.log("Redirecting to PhonePe payment URL:", response.data.redirectUrl);
        window.location.href = response.data.redirectUrl;
      } else {
        throw new Error(response.data.error || "Failed to create payment session");
      }
    } catch (err) {
      console.error("PhonePe payment error:", err);
      console.error("Error response:", err.response?.data);
      console.error("Error status:", err.response?.status);

      let errorMessage = "Payment initiation failed";

      if (err.response?.status === 401 || err.message.includes("not logged in")) {
        errorMessage = "Authentication failed. Please login again.";
        // Add login button to error message
        setTimeout(() => {
          if (window.confirm("You need to login to make a payment. Redirect to login page?")) {
            window.location.href = "/login";
          }
        }, 2000);
      } else if (err.response?.status === 403) {
        errorMessage = "Access denied. Please check your permissions.";
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const plans = [
    {
      name: "Silver",
      price: 1000,
      duration: "1 Month",
      originalPrice: 1500,
      savings: 500,
      popular: false,
      features: [
        "10 Profile Views ",
        "Standard Email Support",
        "Profile Verification Badge",
        "Basic Search Filters"
      ],
      color: "#8E9AAF",
      gradient: "linear-gradient(135deg, #8E9AAF 0%, #C0C0C0 100%)",
      icon: "🥈"
    },
    {
      name: "Gold",
      price: 2000,
      duration: "3 Months",
      originalPrice: 3000,
      savings: 1000,
      popular: true,
      features: [
        "20 Profile Views",
        "Priority Chat Support",
        "Message Read Receipts",
        "Advanced Search & Filters",

      ],
      color: "#FFD700",
      gradient: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
      icon: "👑"
    },
    {
      name: "Diamond",
      price: 3000,
      duration: "6 Months",
      originalPrice: 4500,
      savings: 1500,
      popular: false,
      features: [
        "35 Profile Views",
        "24/7 Phone & Chat Support",
        "Top Search Placement",
        "Personal Relationship Manager",
        // "Priority Profile Review"
      ],
      color: "#B9F2FF",
      gradient: "linear-gradient(135deg, #B9F2FF 0%, #87CEEB 100%)",
      icon: "💎"
    }
  ];

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner-border" style={{ color: "#e84393" }} role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <h3 style={{ marginTop: "20px", color: "#333" }}>Redirecting to Payment...</h3>
      </div>
    );
  }

  return (
    <div className="enhanced-phonepe-container">
      {error && (
        <div className="premium-error-alert">
          <div className="error-content">
            <span className="error-icon">⚠️</span>
            <div className="error-text">
              <strong>Payment Error</strong>
              <p>{error}</p>
            </div>
            <button onClick={() => setError(null)} className="error-close">×</button>
          </div>
        </div>
      )}

      <div className="premium-plans-grid">
        {plans.map((plan, index) => (
          <div
            key={plan.name}
            className={`premium-plan-card ${plan.popular ? 'most-popular' : ''} ${activePlanName === plan.name ? 'active-plan' : ''}`}
            style={{ '--plan-color': plan.color }}
          >
            {plan.popular && (
              <div className="popularity-badge">
                <span>🔥 Most Popular</span>
              </div>
            )}
            {activePlanName === plan.name && (
              <div className="active-badge">
                <span>✅ Active</span>
              </div>
            )}

            <div className="plan-header-enhanced" style={{ background: plan.gradient }}>
              <div className="plan-icon">{plan.icon}</div>
              <h3 className="plan-name">{plan.name}</h3>
              <div className="plan-duration">{plan.duration}</div>
            </div>

            <div className="pricing-section">
              <div className="price-container">
                <div className="original-price">₹{plan.originalPrice}</div>
                <div className="current-price">
                  <span className="currency">₹</span>
                  <span className="amount">{plan.price}</span>
                </div>
                <div className="savings-badge">
                  Save ₹{plan.savings}
                </div>
              </div>
            </div>

            <div className="features-section">
              <h4>What's Included:</h4>
              <ul className="premium-features-list">
                {plan.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="premium-feature">
                    <span className="feature-check">✨</span>
                    <span className="feature-text">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="plan-action">
              <button
                onClick={() => handlePhonePePayment(plan.name, plan.price)}
                disabled={loading || activePlanName === plan.name}
                className={`premium-subscribe-btn ${plan.popular ? 'popular-btn' : ''} ${activePlanName === plan.name ? 'disabled' : ''}`}
                style={{ background: plan.gradient }}
              >
                <span className="btn-text">
                  {loading ? (
                    <>
                      <span className="loading-spinner"></span>
                      Processing...
                    </>
                  ) : (
                    <>
                      <span className="btn-icon">{activePlanName === plan.name ? '✔️' : '🚀'}</span>
                      {activePlanName === plan.name ? 'Current Plan' : `Choose ${plan.name}`}
                    </>
                  )}
                </span>
              </button>


            </div>
          </div>
        ))}
      </div>

      <div className="premium-security-section">
        <div className="security-container-enhanced">
          <div className="security-header">
            <span className="security-icon">🛡️</span>
            <h3>Secure & Trusted Payments</h3>
          </div>

          <div className="payment-methods-grid">
            <div className="payment-method-item">
              <span className="method-icon">📱</span>
              <span>UPI & Digital Wallets</span>
            </div>
            <div className="payment-method-item">
              <span className="method-icon">💳</span>
              <span>Credit & Debit Cards</span>
            </div>
            <div className="payment-method-item">
              <span className="method-icon">🏦</span>
              <span>Net Banking</span>
            </div>
          </div>

          <div className="trust-indicators">
            <div className="trust-item">
              <span>✅ PCI DSS Certified</span>
            </div>
            <div className="trust-item">
              <span>✅ Trusted by 50,000+ Users</span>
            </div>
            <div className="trust-item">
              <span>✅ Zero Hidden Charges</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PhonePePayment;